import apiService from './api.service';
import { API_ENDPOINTS } from '@/constants/api.constants';
import { logger } from '@/utils/logger';
import type { ApiResponse } from '@/types/api';
import type { TournamentInvitation } from '@/types/api.types';

export class TournamentInvitationService {
  /**
   * Получить все приглашения для турнира
   */
  static async getTournamentInvitations(tournamentId: number): Promise<TournamentInvitation[]> {
    try {
      const { data: response } = await apiService.get<ApiResponse<TournamentInvitation[]>>(
        API_ENDPOINTS.TOURNAMENT_INVITATIONS.TOURNAMENT(tournamentId)
      );
      return response.data;
    } catch (error) {
      logger.error('Error fetching tournament invitations:', error);
      throw error;
    }
  }

  /**
   * Получить все приглашения для текущего клуба
   */
  static async getMyClubInvitations(): Promise<TournamentInvitation[]> {
    try {
      const { data: response } = await apiService.get<ApiResponse<TournamentInvitation[]>>(
        API_ENDPOINTS.TOURNAMENT_INVITATIONS.MY_CLUB
      );
      return response.data;
    } catch (error) {
      logger.error('Error fetching club invitations:', error);
      throw error;
    }
  }

  /**
   * Пригласить клуб на турнир
   */
  static async inviteClub(tournamentId: number, clubId: number): Promise<TournamentInvitation> {
    try {
      const { data: response } = await apiService.post<ApiResponse<TournamentInvitation>>(
        API_ENDPOINTS.TOURNAMENT_INVITATIONS.INVITE_CLUB(tournamentId),
        { clubId }
      );
      return response.data;
    } catch (error) {
      logger.error('Error inviting club:', error);
      throw error;
    }
  }

  /**
   * Пригласить несколько клубов на турнир
   */
  static async inviteClubs(tournamentId: number, clubIds: number[]): Promise<TournamentInvitation[]> {
    try {
      const { data: response } = await apiService.post<ApiResponse<TournamentInvitation[]>>(
        API_ENDPOINTS.TOURNAMENT_INVITATIONS.INVITE_CLUBS_BULK(tournamentId),
        { clubIds }
      );
      return response.data;
    } catch (error) {
      logger.error('Error inviting clubs:', error);
      throw error;
    }
  }

  /**
   * Принять приглашение
   * userId берется из сессии на сервере
   */
  static async acceptInvitation(invitationId: number): Promise<TournamentInvitation> {
    try {
      const { data: response } = await apiService.post<ApiResponse<TournamentInvitation>>(
        API_ENDPOINTS.TOURNAMENT_INVITATIONS.ACCEPT(invitationId)
      );
      return response.data;
    } catch (error: any) {
      logger.error('Error accepting invitation:', error);
      throw error;
    }
  }

  /**
   * Отклонить приглашение
   * userId берется из сессии на сервере
   */
  static async rejectInvitation(invitationId: number): Promise<TournamentInvitation> {
    try {
      const { data: response } = await apiService.post<ApiResponse<TournamentInvitation>>(
        API_ENDPOINTS.TOURNAMENT_INVITATIONS.REJECT(invitationId)
      );
      return response.data;
    } catch (error: any) {
      logger.error('Error rejecting invitation:', error);
      throw error;
    }
  }

  /**
   * Удалить приглашение
   * userId берется из сессии на сервере
   */
  static async deleteInvitation(invitationId: number): Promise<void> {
    try {
      await apiService.delete(API_ENDPOINTS.TOURNAMENT_INVITATIONS.DELETE(invitationId));
    } catch (error: any) {
      logger.error('Error deleting invitation:', error);
      throw error;
    }
  }
}

export default TournamentInvitationService;